import { useRef } from "react";
import { Popover } from "@headlessui/react";

import { type DocumentSet } from "@hsl/core/documents/types";
import cx from "@hsl/core/utils/cx";
import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";
import { type DocObj, type TopLevelDataObj } from "@hsl/fund-explorer/types";

import { Translate } from "../../../../hooks/useTranslation";
import CSVIcon from "../../../CsvIcon/CsvIcon";
import DocumentCheckbox from "../../../FundCardDocuments/components/DocumentCheckbox";
import PDFIcon from "../../../PdfIcon/PdfIcon";
import { redDot } from "../../../Subscription/SubscriptionButtons/SubscriptionButtons";
import { DocDisplayPanel } from "./components/DocDisplayPanel";
import { DocElem } from "./components/DocElem";

export const DATE_FORMAT = "DD MMM YYYY";

interface DocumentDisplayProps {
    data: DocObj[] | string;
    icon: JSX.Element;
    type?: string;
    showName?: boolean;
    showLang?: boolean;
    offsetPanelTop?: number;
    offsetPanelLeft?: number;
    shareClass?: TopLevelDataObj;
    fund?: FundDataWithType;
    onClick?: () => void;
}
export const DocumentDisplay = ({
    data,
    icon,
    type,
    showName,
    showLang,
    offsetPanelTop = 20,
    offsetPanelLeft = -65,
    shareClass,
    fund,
    onClick,
}: DocumentDisplayProps) => {
    const ref = useRef<HTMLDivElement>(null);

    if (!data) {
        return undefined;
    }
    if (typeof data === "string") {
        return data;
    }
    if (data.length === 1) {
        return (
            <DocElem
                {...data[0]!}
                icon={icon}
                showName={showName}
                showLang={showLang}
                type={type}
                shareClass={shareClass}
                fund={fund}
                onClick={onClick}
            />
        );
    }

    return (
        <div ref={ref}>
            <Popover as="div" className="relative flex items-center">
                {({ close }) => {
                    const refPos = ref.current?.getBoundingClientRect();
                    return (
                        <>
                            <Popover.Button className="flex">
                                {icon}
                                {showName && (
                                    <span className="pl-2">
                                        <Translate>{`${
                                            data[0]?.name ?? ""
                                        }`}</Translate>
                                    </span>
                                )}
                            </Popover.Button>

                            <Popover.Panel
                                className="fixed inset-0 z-50"
                                onClick={close}
                            >
                                <DocDisplayPanel
                                    data={data}
                                    top={
                                        refPos?.top
                                            ? refPos.top + offsetPanelTop
                                            : 0
                                    }
                                    left={
                                        refPos?.left
                                            ? refPos.left + offsetPanelLeft
                                            : 0
                                    }
                                    close={close}
                                />
                            </Popover.Panel>
                        </>
                    );
                }}
            </Popover>
        </div>
    );
};

export const DocumentDisplayListWithCheckbox = ({
    data,
    selectionActive,
    showLang,
    shareClass,
    hasUpdates: hasUpdatesFn,
    markRead,
}: {
    data: DocumentSet[][];
    selectionActive: boolean;
    showLang?: boolean;
    shareClass?: TopLevelDataObj;
    hasUpdates?: (x: string | number) => boolean;
    markRead?: (x: string | number) => void;
}) => {
    return (
        <>
            {data.map((docArr) => {
                const { id, url, language_aware_display_name } = docArr[0]!;
                const fileType = url.split(".").pop()?.toLowerCase();
                const computedVals = docArr.map((doc) => ({
                    lang: doc.language,
                    name:
                        doc.language === "English"
                            ? doc.display_name
                            : doc.language_aware_display_name,
                    url: doc.url,
                    asAtDate: doc.date,
                }));
                const hasUpdates = docArr.some(
                    (d) => hasUpdatesFn && hasUpdatesFn(d.id),
                );
                return (
                    <span
                        key={id}
                        className="relative my-2 flex items-center justify-between"
                    >
                        <span
                            className={cx(
                                hasUpdates && redDot,
                                "before:left-[10px] before:right-auto",
                            )}
                        >
                            <DocumentDisplay
                                icon={
                                    fileType === "pdf" ? (
                                        <PDFIcon />
                                    ) : (
                                        <CSVIcon />
                                    )
                                }
                                type={fileType}
                                data={computedVals}
                                offsetPanelLeft={20}
                                showName
                                showLang={showLang}
                                shareClass={shareClass}
                                onClick={() =>
                                    docArr.map((d) => markRead?.(d.id))
                                }
                            />
                        </span>
                        {selectionActive && (
                            <DocumentCheckbox
                                value={computedVals}
                                name={language_aware_display_name}
                                containerClassName="py-0 ml-4"
                            />
                        )}
                    </span>
                );
            })}
        </>
    );
};
